import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserFriends, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Form, Button, InputGroup, Table, Badge, Spinner } from '@themesberg/react-bootstrap';
import { collection, getDocs, query, updateDoc, doc, where } from 'firebase/firestore';
import { db } from '../../config/firebase';

export default () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'users'),
        where('isTestUser', '==', true)
      );
      const snapshot = await getDocs(q);
      const accountsList = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        accountsList.push({
          id: doc.id,
          name: data.name || '',
          gender: data.gender || '',
          age: data.age || 0,
          profileImageUrl: data.profileImageUrl || null,
          isCityClusterProfile: data.isCityClusterProfile || false,
          createdAt: data.createdAt || null
        });
      });

      // Filter by creation time
      accountsList.sort((a, b) => {
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
        return timeB - timeA;
      });

      setAccounts(accountsList);
      setError(null);
    } catch (err) {
      console.error("Error loading accounts:", err);
      setError("Hesaplar yüklenirken hata oluştu: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCityClusterStatus = async (accountId, currentStatus) => {
    try {
      setUpdatingId(accountId);
      await updateDoc(doc(db, 'users', accountId), {
        isCityClusterProfile: !currentStatus
      });
      
      setAccounts(prev => prev.map(acc => {
        if (acc.id === accountId) {
          return { ...acc, isCityClusterProfile: !currentStatus };
        }
        return acc;
      }));
    } catch (err) {
      console.error("Error updating cluster status:", err);
      alert("Durum güncellenirken hata oluştu: " + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredAccounts = accounts.filter(account => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return account.name.toLowerCase().includes(searchLower);
  });

  const cityClusterCount = accounts.filter(a => a.isCityClusterProfile).length;

  return (
    <>
      <div className="py-4">
        <h4>Şehir Kümesi (Ortak Küme)</h4>
        <p className="text-muted mb-0">Hesaplar / Şehir Kümesi</p>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="alert alert-primary mb-4">
            <h5 className="alert-heading d-flex align-items-center">
               <FontAwesomeIcon icon={faUserFriends} className="me-2" />
               Ortak Şehir Kümesi Nedir?
            </h5>
            <p className="mb-0">
              Buradan seçeceğiniz test kullanıcıları Uygulamada <b>özel bir sekmede (Örn. kişinin kendi şehri)</b> listelenir.
              Bu hesaplar, müşteri uygulamada hangi şehri seçerse seçsin, sanki o şehirdeymiş gibi müşteriye gösterilir.
              <b>Şu anda kümeye ekli üye sayısı: {cityClusterCount}</b>
            </p>
          </div>

          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Profil Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center p-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                <thead className="thead-light">
                  <tr>
                    <th className="border-bottom">#</th>
                    <th className="border-bottom">Profil</th>
                    <th className="border-bottom">Ad Soyad</th>
                    <th className="border-bottom">Yaş</th>
                    <th className="border-bottom">Cinsiyet</th>
                    <th className="border-bottom text-center">Şehir Kümesine Ekle</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center p-4">
                        {searchTerm ? "Arama sonucu bulunamadı." : "Henüz test kullanıcısı eklenmemiş."}
                      </td>
                    </tr>
                  ) : (
                    filteredAccounts.map((account, index) => (
                      <tr key={account.id} className={account.isCityClusterProfile ? 'table-primary' : ''}>
                        <td className="align-middle">{index + 1}</td>
                        <td className="align-middle">
                          {account.profileImageUrl ? (
                            <img
                              src={account.profileImageUrl}
                              alt={account.name}
                              className="rounded-circle"
                              style={{ width: 40, height: 40, objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary d-inline-block"
                              style={{ width: 40, height: 40 }}
                            ></div>
                          )}
                        </td>
                        <td className="align-middle fw-bold">{account.name}</td>
                        <td className="align-middle">{account.age}</td>
                        <td className="align-middle">
                          <Badge bg={account.gender === "Kadın" ? "danger" : "primary"} className="badge-sm">
                            {account.gender}
                          </Badge>
                        </td>
                        <td className="align-middle text-center">
                          <Button
                            variant={account.isCityClusterProfile ? "success" : "outline-secondary"}
                            size="sm"
                            className="w-100 fw-bold"
                            style={{ maxWidth: '180px' }}
                            onClick={() => toggleCityClusterStatus(account.id, account.isCityClusterProfile)}
                            disabled={updatingId === account.id}
                          >
                            {updatingId === account.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : account.isCityClusterProfile ? (
                              <><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Kümede</>
                            ) : (
                              "Kümeye Ekle"
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};
